import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Loader2, Download, CheckCircle2, TriangleAlert } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

// Minimal CSV parser (same tuning used in the staff bulk dialog).
function parseCsv(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else { field += c; }
    } else {
      if (c === '"') { inQuotes = true; }
      else if (c === ',') { cur.push(field); field = ""; }
      else if (c === '\n' || c === '\r') {
        if (c === '\r' && text[i + 1] === '\n') i++;
        cur.push(field); field = "";
        if (cur.some((x) => x.trim().length > 0)) rows.push(cur);
        cur = [];
      } else {
        field += c;
      }
    }
  }
  if (field.length > 0 || cur.length > 0) { cur.push(field); rows.push(cur); }
  return rows;
}

function rowsToIngredients(rows) {
  if (!rows.length) return { parsed: [], warnings: ["The file appears empty."] };
  const [head, ...body] = rows;
  const headers = head.map((h) => h.trim().toLowerCase());
  const iName = headers.indexOf("name");
  if (iName < 0) return { parsed: [], warnings: ["Missing required 'name' column."] };
  const map = {
    name: iName,
    category: headers.indexOf("category"),
    purchase_qty: headers.indexOf("purchase_qty"),
    purchase_unit: headers.indexOf("purchase_unit"),
    purchase_unit_kind: headers.indexOf("purchase_unit_kind"),
    purchase_price: headers.indexOf("purchase_price"),
    yield_percent: headers.indexOf("yield_percent"),
    prep_time_minutes: headers.indexOf("prep_time_minutes"),
    allergens: headers.indexOf("allergens"),
    notes: headers.indexOf("notes"),
  };
  const parsed = [];
  const warnings = [];
  body.forEach((r, i) => {
    const get = (k) => (map[k] >= 0 && r[map[k]] != null ? String(r[map[k]]).trim() : "");
    const name = get("name");
    if (!name) { warnings.push(`Row ${i + 2}: missing name (skipped).`); return; }
    const num = (k, fallback) => {
      const v = parseFloat(get(k));
      return Number.isFinite(v) && v > 0 ? v : fallback;
    };
    parsed.push({
      name,
      category: get("category") || "food",
      purchase_qty: num("purchase_qty", 1),
      purchase_unit: get("purchase_unit") || "kg",
      purchase_unit_kind: get("purchase_unit_kind") || "weight",
      purchase_price: parseFloat(get("purchase_price") || "0") || 0,
      yield_percent: num("yield_percent", 100),
      prep_time_minutes: parseFloat(get("prep_time_minutes") || "0") || 0,
      allergens: get("allergens") || null,
      notes: get("notes") || null,
    });
  });
  return { parsed, warnings };
}

export default function BulkIngredientsImportDialog({ open, onOpenChange, onImported }) {
  const [text, setText] = useState("");
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const [importing, setImporting] = useState(false);

  const parsed = useMemo(() => {
    if (!text.trim()) return { parsed: [], warnings: [] };
    try {
      return rowsToIngredients(parseCsv(text));
    } catch {
      return { parsed: [], warnings: ["Could not parse the CSV. Check formatting."] };
    }
  }, [text]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!/\.csv$/i.test(file.name) && file.type && !file.type.includes("csv") && !file.type.includes("text")) {
      toast.error("Upload a .csv file.");
      return;
    }
    setText(await file.text());
    setFileName(file.name);
    setResult(null);
  };

  const downloadTemplate = async () => {
    try {
      const resp = await api.get("/ingredients/csv-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "culinaire-ingredients-template.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download template.");
    }
  };

  const doImport = async () => {
    if (!parsed.parsed.length) return;
    setImporting(true);
    try {
      const { data } = await api.post("/ingredients/bulk-import", {
        rows: parsed.parsed,
        upsert_on_name: true,
      });
      setResult(data);
      const errs = data.error_count || 0;
      if (errs === 0) toast.success(`Imported: ${data.created} new, ${data.updated} updated.`);
      else toast.message(`Imported: ${data.created} new, ${data.updated} updated, ${errs} errored.`);
      if (onImported) onImported();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Import failed.");
    } finally {
      setImporting(false);
    }
  };

  const reset = () => { setText(""); setFileName(""); setResult(null); };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="glass-card border-white/10 max-w-2xl" data-testid="bulk-ing-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2">
            <Upload className="w-5 h-5 text-amber-200" />
            Bulk import ingredients
          </DialogTitle>
          <DialogDescription className="text-white/60 text-sm">
            Upload a CSV or paste rows below. Existing ingredients with matching names are updated in place, so re-running the same file during a seasonal menu switch never duplicates a row.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={downloadTemplate} className="gap-2" data-testid="bulk-ing-template">
              <Download className="w-3.5 h-3.5" /> Download template
            </Button>
            <label className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-white/15 hover:bg-white/5 text-sm cursor-pointer" data-testid="bulk-ing-file-label">
              <FileText className="w-3.5 h-3.5" /> {fileName ? `File: ${fileName}` : "Choose .csv file"}
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
                data-testid="bulk-ing-file"
              />
            </label>
            {fileName && (
              <button onClick={reset} className="text-xs text-white/60 hover:text-white/90 underline">clear</button>
            )}
          </div>

          <div className="space-y-1.5">
            <div className="text-xs text-white/60">
              Or paste CSV rows here. Columns: name, category, purchase_qty, purchase_unit, purchase_unit_kind, purchase_price, yield_percent, prep_time_minutes, allergens, notes.
            </div>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={7}
              placeholder="name,category,purchase_qty,purchase_unit,purchase_unit_kind,purchase_price,yield_percent,prep_time_minutes,allergens,notes
Aged Cheddar,food,1,kg,weight,28.10,100,2,dairy,Australian
Fresh Basil Bunch,food,1,each,count,2.60,100,0,,Snip stems before service"
              className="font-mono text-xs"
              data-testid="bulk-ing-paste"
            />
          </div>

          {parsed.warnings.length > 0 && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100 space-y-1">
              {parsed.warnings.slice(0, 5).map((w, i) => (
                <div key={i} className="flex items-center gap-2">
                  <TriangleAlert className="w-3 h-3" /> {w}
                </div>
              ))}
            </div>
          )}

          {parsed.parsed.length > 0 && !result && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="bulk-ing-preview">
              <div className="label-eyebrow mb-2">Preview: {parsed.parsed.length} rows</div>
              <div className="max-h-40 overflow-y-auto text-xs tabular space-y-1 pr-1">
                {parsed.parsed.slice(0, 8).map((r, i) => (
                  <div key={i} className="flex items-center justify-between text-white/80">
                    <span>{r.name} <span className="text-white/40">({r.category})</span></span>
                    <span className="text-white/50">{r.purchase_qty} {r.purchase_unit}, {r.purchase_price} at {r.yield_percent}% yield</span>
                  </div>
                ))}
                {parsed.parsed.length > 8 && (
                  <div className="text-white/40">... and {parsed.parsed.length - 8} more</div>
                )}
              </div>
            </div>
          )}

          {result && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-100 space-y-1" data-testid="bulk-ing-result">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> Import complete.
              </div>
              <div className="text-xs text-emerald-100/85">
                {result.created} new, {result.updated} updated, {result.error_count} errored out of {result.total_rows} rows.
              </div>
              {(result.errors || []).slice(0, 3).map((e, i) => (
                <div key={i} className="text-xs text-rose-200">Row {e.row}: {e.error}</div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="bulk-ing-cancel">Close</Button>
          <Button onClick={doImport} disabled={importing || parsed.parsed.length === 0} data-testid="bulk-ing-import">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            <span className="ml-1.5">Import {parsed.parsed.length || ""} row{parsed.parsed.length === 1 ? "" : "s"}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
